import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useSession } from '../../state/useSession';
import { takeUpEmailLink, useBuildRun } from '../../state/buildRun';
import { readIntent } from '../../state/buildIntent';
import { emailMeTheLink, openEmailLink } from '../../api/emailLink';
import { ApiError } from '../../api/client';
import { useDraft } from '../../state/draft';
import { stashDraft, type StashResult } from '../../state/draftStash';
import { notKept } from './Plan';
import { creditBalance, startCheckout } from '../../api/billing';
import { offerFor, rememberCheckoutOffer, reportInitiateCheckout, TRIAL_DAYS, type Offer } from '../../state/trial';
import { leaveFor } from '../../platform/leave';
import { funnelStep, useFunnelStep } from '../../analytics/funnel';
import { secondsBucket } from '../../analytics/visits';
import type { AppPlan } from '@/webapp/plan/planTypes';
import SignUp from '../auth/SignUp';
import Paywall from './Paywall';
import ExitQuestion, { exitQuestionAsked } from './ExitQuestion';
import { Button, Screen, TextField } from '../../ui';

/** How long their own site is theirs to look at before the offer slides up (owner, 2026-10-08). */
export const PREVIEW_BEFORE_OFFER_MS = 15_000;

/**
 * /new/building — the owner's flow (2026-10-08): the site renders while the account is made, is shown
 * once it is, and the offer comes over it a little later.
 *
 *  1. Building: their app's name and the step it is on — and, signed out, the account box (Apple or
 *     email). The build runs under the browser's id meanwhile (state/buildRun.ts).
 *  2. Signed in and built: their own site, full screen, framed and unpublished.
 *  3. After PREVIEW_BEFORE_OFFER_MS: the trial offer slides up over the dimmed site and stays. Closed,
 *     it leaves a bar that opens it again; the site stays dimmed. The card is the way on.
 *
 * Subscribers never come here: Build builds and opens the editor, as before.
 */
export default function Building() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isInitializing } = useSession();
  const status = useBuildRun((s) => s.status);
  const phase = useBuildRun((s) => s.phase);
  const releasing = useBuildRun((s) => s.releasing);
  const heldApp = useBuildRun((s) => s.heldApp);
  const error = useBuildRun((s) => s.error);
  const outOfCredits = useBuildRun((s) => s.outOfCredits);
  const draft = useDraft();
  const appName = heldApp?.name || draft.plan?.appName || draft.appName || 'your app';
  const [nothing, setNothing] = useState(false);

  /*
   * Arrived by an "email me my app link" email (?link=): probably not the browser the build began
   * in. The link is traded for the build's choices, this browser takes them up, and the sign-up
   * below — the address already in it — collects the build here.
   */
  const [params, setParams] = useSearchParams();
  const [link, setLink] = useState<'none' | 'opening' | 'opened' | 'gone'>(() => (params.get('link') ? 'opening' : 'none'));
  const [linkEmail, setLinkEmail] = useState('');
  useEffect(() => {
    const token = params.get('link');
    if (!token) return;
    let live = true;
    openEmailLink(token).then(
      (opened) => {
        if (!live) return;
        takeUpEmailLink(opened);
        setLinkEmail(opened.email);
        setLink('opened');
        funnelStep('email_link_opened', { code: opened.state });
      },
      (e: unknown) => {
        if (!live) return;
        setLink('gone');
        funnelStep('email_link_opened', { code: e instanceof ApiError && e.code === 'bad_link' ? 'bad_link' : 'gone' });
      },
    ).finally(() => {
      // The token is not left in the address bar, the history or a shared screenshot.
      if (live) setParams((p) => { p.delete('link'); return p; }, { replace: true });
    });
    return () => {
      live = false;
    };
    // Once, for the link the page was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Signed in (just now, or after a reload — Apple's return is one): ask again for the build on
   * record under its key. A build begun signed out is claimed by this account; a held one comes back
   * with its preview; a running one is waited for.
   */
  const collected = useRef(false);
  useEffect(() => {
    if (isInitializing || !isAuthenticated || !user || collected.current || link === 'opening') return;
    /*
     * Only from rest: a build that is running is waited for, and one that has just ENDED here — a
     * refusal for credits, a failure — is not asked for again. It was, the moment the refusal landed,
     * so one press of Build sent two requests and drew two refusals (2026-10-09).
     */
    if (status !== 'idle' || heldApp?.previewUrl) return;
    collected.current = true;
    void useBuildRun.getState().collect(user.id).then((found) => {
      if (!found) setNothing(true);
    });
  }, [isInitializing, isAuthenticated, user, status, heldApp, link]);

  // A build that landed outright (an account that could pay for it) opens the editor by itself
  // (state/buildRun.ts navigates there), so this screen has nothing to add for it.

  /*
   * The server would hold no build today (one a day per account): nothing to show, so the offer
   * itself — the same panel, the same checkout, and the build follows the card.
   */
  if (isAuthenticated && status === 'error' && outOfCredits) {
    return <SiteThenOffer appName={appName} preview={null} plan={draft.plan} ownerId={user?.id ?? ''} />;
  }

  const showSite = isAuthenticated && !!heldApp?.previewUrl;
  if (showSite) return <SiteThenOffer appName={appName} preview={heldApp!.previewUrl!} plan={draft.plan} ownerId={user?.id ?? ''} />;

  if (link === 'opening') {
    return (
      <Screen>
        <p className="type-body building-step" role="status">Opening your app…</p>
      </Screen>
    );
  }

  if (link === 'gone') {
    return (
      <Screen title="That app is no longer kept">
        <p className="type-body">We keep an app for 3 days, or until an account collects it. Describe it again and Saddle builds it afresh.</p>
        <Button title="Start from your idea" onPress={() => navigate('/new/idea')} />
      </Screen>
    );
  }

  if (nothing && status !== 'running') {
    return (
      <Screen title="Nothing is building">
        <p className="type-body">There’s no app being built in this browser right now.</p>
        <Button title="Start from your idea" onPress={() => navigate('/new/idea')} />
      </Screen>
    );
  }

  /*
   * V6 polish (2026-10-14): the RELEASE of an app the server already holds (state/buildRun.ts
   * `releasing`: the replay after sign-in, the free-week replay), or a build already at its publish,
   * is not a build. One calm line: no clock restarting at 0:06 "usually about 4 minutes", no stage
   * list back at "Reading your plan" over an app that exists.
   */
  if (isAuthenticated && status === 'running' && (releasing || phase === 'publishing')) {
    return (
      <Screen>
        <div className="building building-going-live" role="status" aria-live="polite">
          <span className="building-going-live-spinner" aria-hidden="true" />
          <p className="type-heading building-going-live-line">Your {appName} is built.</p>{' '}
          <p className="type-body building-going-live-sub">Putting it live…</p>
        </div>
      </Screen>
    );
  }

  const built = !!heldApp; // held, signed out: ready, waiting for the account
  const included = draft.plan?.features.filter((f) => f.included) ?? [];
  /*
   * Signed out and building: the account is asked for on this same screen, and "Continue with email"
   * must be on a 390x844 screen without scrolling (V6 polish, 2026-10-14). So the page says "Your …
   * is being built" once (the card does not say it again), the stage is one line, and what is going
   * in folds behind "What’s going in".
   */
  const askingAccount = !isInitializing && !isAuthenticated && status === 'running' && !built;
  const features = included.length > 0 && (
    <ul className="building-features" aria-label="What’s going in">
      {included.slice(0, 6).map((f) => <li key={f.label}>{f.label}</li>)}
    </ul>
  );
  /* Built with the plan's own features and look (handoff §2); either can be changed on the plan. */
  const change = draft.plan && (
    <p className="type-caption building-change">
      <button type="button" className="link-button" onClick={() => navigate('/new/plan')}>Change the features or look</button>
    </p>
  );
  return (
    <Screen>
      <div className={askingAccount ? 'building building-compact' : 'building'}>
        <h1 className="type-title building-name">{askingAccount ? `Your ${appName} is being built` : appName}</h1>
        {!askingAccount && (
          <p className="type-body building-step" role="status" aria-live="polite">
            {status === 'error'
              ? error ?? 'That build did not finish.'
              : built
                ? 'Your app is built and ready to see.'
                : status === 'running'
                  ? isAuthenticated
                    ? 'You’re in. Your app is being built, and it opens here by itself the moment it’s ready.'
                    : 'Your app is being built right now.'
                  : 'Getting your app ready…'}
          </p>
        )}
        {status === 'running' && <BuildProgress startedAt={buildStartedAt()} compact={askingAccount} />}
        {/* What is going in — the plan they just said yes to — so the wait is never a blank page. */}
        {status === 'running' && (askingAccount ? (
          (features || change) && (
            <details className="building-going-in">
              <summary className="type-caption">What’s going in</summary>
              {features}
              {change}
            </details>
          )
        ) : (
          <>
            {features}
            {change}
          </>
        ))}
      </div>
      {!isInitializing && !isAuthenticated && (
        <SignUp
          card={{
            from: '/new/building',
            // Building: the page above already says "Your … is being built", so the card does not.
            headline: built ? `Create your account to see your ${appName}` : null,
            sub: built
              ? 'It’s ready. Your account keeps it, and you see it the moment you’re in.'
              : 'Create your account while it builds. You’ll see it the moment it’s ready.',
            ...(linkEmail ? { email: linkEmail } : {}),
          }}
        />
      )}
      {/* Not ready for an account: the link by email instead (not to someone who came by that email). */}
      {!isInitializing && !isAuthenticated && link === 'none' && (status === 'running' || built) && <EmailMeTheLink appName={appName} />}
      {isAuthenticated && status !== 'running' && !heldApp && !nothing && (
        <p className="type-caption">Bringing your app back…</p>
      )}
    </Screen>
  );
}

/**
 * "Email me my app link" (reduce-friction handoff #2): one quiet line under the sign-up, opening to
 * one field. No account and no code — the link, and one reminder, to an address they give.
 */
function EmailMeTheLink({ appName }: { appName: string }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  if (sentTo) {
    return (
      <p role="status" className="type-body email-link-sent">
        Sent to {sentTo}. Open it on any phone or computer and create your account there to see your {appName}. We keep it for 3 days.
      </p>
    );
  }
  if (!open) {
    return (
      <p className="email-link-offer">
        <button type="button" className="link-button" onClick={() => setOpen(true)}>Not now? Email me a link to my app</button>
      </p>
    );
  }

  async function send(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (sending) return;
    const address = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setProblem('Check the email address.');
      return;
    }
    const intent = readIntent();
    if (!intent?.requestKey) {
      setProblem('There’s no app building in this browser to send.');
      return;
    }
    setSending(true);
    setProblem(null);
    try {
      await emailMeTheLink({
        email: address,
        requestKey: intent.requestKey,
        goal: intent.goal,
        appName: intent.appName,
        plan: intent.plan ?? useDraft.getState().plan ?? null,
      });
      funnelStep('email_captured', { where: 'wait' });
      setSentTo(address);
    } catch (err) {
      setProblem(err instanceof ApiError && err.reason ? err.reason : 'That email didn’t send. Try again in a moment.');
    } finally {
      setSending(false);
    }
  }

  return (
    <form className="email-link" onSubmit={(e) => void send(e)} noValidate>
      <TextField
        label="Your email for the link"
        type="email"
        value={email}
        onChangeText={setEmail}
        autoComplete="email"
        inputMode="email"
        error={problem ?? undefined}
      />
      <Button title="Email me the link" loading={sending} type="submit" />
      <p className="type-caption email-link-terms">We send the link and one reminder, nothing else. Unsubscribe from either.</p>
    </form>
  );
}

/** About how long a build takes end to end — what this screen has always told people. */
export const EXPECTED_BUILD_MS = 4 * 60_000;

/*
 * The build's real stages (src/webapp/generateWebApp.ts: write, check, repair, assemble), at the
 * times they usually begin. The server answers once, at the end, so these follow the clock; the
 * last holds until the app arrives.
 */
const STAGES = [
  { at: 0, label: 'Reading your plan' },
  { at: 15_000, label: 'Writing the pages' },
  { at: 150_000, label: 'Checking every button works' },
  { at: 210_000, label: 'Putting it together' },
];

/** When this build began: its key's age, which a sign-in in the middle does not reset. */
function buildStartedAt(): number {
  const at = Date.parse(readIntent()?.startedAt ?? '');
  return Number.isFinite(at) ? at : Date.now();
}

/**
 * Progress people can see (2026-10-09): a visitor left the build screen 18 seconds in, and another
 * 7 seconds after signing in, in front of a sliver of a bar that never moved and a line that never
 * changed for four minutes. A clock, a bar that fills, and the stage it is at.
 */
function BuildProgress({ startedAt, compact = false }: { startedAt: number; compact?: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);
  const elapsed = Math.max(0, now - startedAt);
  // Never full before it is done: the last stretch waits on the app itself.
  const pct = Math.min(95, Math.round((elapsed / EXPECTED_BUILD_MS) * 100));
  const stage = STAGES.filter((s) => elapsed >= s.at).length - 1;
  const seconds = Math.floor(elapsed / 1000);
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  return (
    <div className="building-progress">
      <div className="building-meter" role="progressbar" aria-label="Building your app" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <p className="type-caption building-elapsed">{clock} so far · usually about 4 minutes</p>
      {compact ? (
        // While the account is asked for: the stage it is at, as one line (V6 polish).
        <ul className="plan-progress building-stages building-stages-compact" role="status" aria-live="polite">
          <li className="plan-progress-now">
            {STAGES[stage].label}
            <span className="building-stage-count"> · step {stage + 1} of {STAGES.length}</span>
          </li>
        </ul>
      ) : (
        <ul className="plan-progress building-stages">
          {STAGES.map((s, i) => (
            <li key={s.label} className={i < stage ? 'plan-progress-done' : i === stage ? 'plan-progress-now' : 'building-stage-later'}>
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Their site, then the offer over it. */
/** `preview`: the URL the site serves the held app at, or null when there is nothing to show. */
function SiteThenOffer({ appName, preview, plan, ownerId }: { appName: string; preview: string | null; plan: AppPlan | null; ownerId: string }) {
  const navigate = useNavigate();
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerShown, setOfferShown] = useState(false);
  const [offer, setOffer] = useState<Offer>('trial');
  const [giftEnded, setGiftEnded] = useState<'used_up' | 'expired' | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** What the trip to Stripe cannot carry, once said: the next press goes on (as on the plan). */
  const [notKeptSaid, setNotKeptSaid] = useState<StashResult | null>(null);

  // Back from Stripe by the browser's Back (the page restored as it was left): the button is given back.
  useEffect(() => {
    const shown = (e: PageTransitionEvent): void => {
      if (e.persisted) setBusy(false);
    };
    window.addEventListener('pageshow', shown);
    return () => window.removeEventListener('pageshow', shown);
  }, []);
  useFunnelStep('site_preview_viewed', preview !== null);
  useFunnelStep('paywall_viewed', offerOpen, { offer });

  useEffect(() => {
    void creditBalance()
      .then((b) => {
        setOffer(offerFor(b));
        setGiftEnded(b.gift?.ended ?? null);
      })
      .catch(() => undefined);
    // With a site to look at, a moment first; without one, the offer at once.
    const timer = setTimeout(() => {
      setOfferOpen(true);
      setOfferShown(true);
    }, preview === null ? 0 : PREVIEW_BEFORE_OFFER_MS);
    return () => clearTimeout(timer);
  }, []);

  /*
   * paywall_left (as on the plan): the card put away, or the page closed with it open — never the
   * trip to Stripe, which is checkout_started.
   */
  const openedAt = useRef<number | null>(null);
  const toCheckout = useRef(false);
  useEffect(() => {
    if (offerOpen) openedAt.current = Date.now();
  }, [offerOpen]);
  function noteLeft(now: boolean): void {
    if (openedAt.current === null || toCheckout.current) return;
    funnelStep('paywall_left', { offer, seconds: secondsBucket(Date.now() - openedAt.current) }, now ? { now: true } : undefined);
    openedAt.current = null;
  }
  useEffect(() => {
    const onHide = (): void => noteLeft(true);
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  });

  const [askingExit, setAskingExit] = useState(false);
  /** The offer put away: over their site, a bar stays and the site stays dimmed; with no site, back to the plan. */
  function putAway(): void {
    if (preview !== null) setOfferOpen(false);
    else navigate('/new/plan');
  }

  async function checkout(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const kept = await stashDraft(ownerId);
    if ((!kept.saved || kept.lost.length > 0) && !notKeptSaid) {
      setNotKeptSaid(kept);
      setBusy(false);
      return;
    }
    rememberCheckoutOffer(offer);
    const result = await startCheckout(
      'subscription',
      (url) => {
        toCheckout.current = true;
        funnelStep('checkout_started', { offer }, { now: true });
        if (reportInitiateCheckout(ownerId, useDraft.getState().description)) setTimeout(() => leaveFor(url), 300);
        else leaveFor(url);
      },
      { resume: 'build' },
    );
    if (result.status === 'redirecting') return;
    setBusy(false);
    setNotice(result.status === 'signin_required' ? 'Your session has ended. Sign in again to start your trial.' : result.message);
  }

  const shownPlan: AppPlan = plan ?? ({ appName, features: [] } as unknown as AppPlan);
  return (
    <div className={offerShown ? 'site-preview site-preview-dimmed' : 'site-preview'}>
      <div className="site-preview-bar type-caption">
        <span>{preview !== null ? `Your ${appName} · preview` : `Your ${appName}`}</span>
      </div>
      {preview !== null ? (
        /*
          Served by the site, not drawn here (api/build.ts, heldPreviewUrl): another origin, so it
          runs with its own storage and can never read the builder's.
        */
        <iframe
          className="site-preview-frame"
          title={`${appName}, a preview`}
          src={preview}
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-forms allow-same-origin"
        />
      ) : (
        <div className="site-preview-frame" aria-hidden="true" />
      )}
      {offerShown && (
        <div className={offerOpen ? 'site-offer site-offer-open' : 'site-offer'} aria-live="polite">
          {askingExit ? (
            <div className="paywall"><ExitQuestion onDone={() => { setAskingExit(false); putAway(); }} /></div>
          ) : offerOpen ? (
            <Paywall
              plan={{ ...shownPlan, appName }}
              offer={offer}
              ready={preview !== null}
              giftEnded={giftEnded}
              busy={busy}
              continueLabel={notKeptSaid ? 'Continue to checkout' : undefined}
              notice={
                <>
                  {notKeptSaid && (
                    <p role="status" className="type-caption plan-detail plan-notice">
                      {notKeptSaid.saved
                        ? notKept(notKeptSaid.lost)
                        : "This browser couldn't keep your plan while you're at checkout, so you'd need to describe it again afterwards."}
                    </p>
                  )}
                  {notice && <p role="alert" className="type-caption field-error-text">{notice}</p>}
                </>
              }
              onContinue={() => void checkout()}
              backLabel={preview !== null ? 'Not now' : 'Back to your plan'}
              onBack={() => {
                noteLeft(false);
                // Asked once, ever, what stopped them (ExitQuestion) — then put away as before.
                if (exitQuestionAsked()) putAway();
                else setAskingExit(true);
              }}
            />
          ) : (
            <div className="site-offer-bar">
              <span className="type-body">Your {appName} is ready to go live.</span>
              <Button title={offer === 'trial' ? `Start my ${TRIAL_DAYS} free days` : 'Subscribe'} onPress={() => setOfferOpen(true)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
